import React, {MutableRefObject, useRef, useState} from 'react';
import ngage from "services/ngage";
import {FieldArrayRenderProps, FormikHelpers} from "formik";
import {Trail, TrailFormCommonData, TrailFormData} from "types/Trail";
import Form, {FormBlock, FormSection} from "components/Form/Form";
import {
  MediaField,
  MultiSelectField,
  SelectField,
  TextAreaField,
  TextField,
  ToggleField
} from "components/Form/Form/Field";
import {TagIcon} from "@heroicons/react/solid";
import {useQuery} from "react-query";
import {toTitleCase} from "services/string";
import IconName from "types/IconName";
import AccessibilityCriterion from "types/AccessibilityCriterion";
import TransportMode from "types/TransportMode";
import * as Yup from 'yup';
import {GolfTrailWaypoint, StandardTrailWaypoint, TrailWaypoint} from "types/TrailWaypoint";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "context/AuthContext";
import {InformationListField, LinkListField} from "components/Form/Form/ListField";
import RequirementListField from "./RequirementListField";
import AdvisoryListField from "./AdvisoryListField";
import {DurationUnit, ToggleSwitch} from "components/Form/Form/Control";
import DurationField from "components/Form/Form/Field/DurationField";
import TrailType from "types/TrailType";
import {TrailAdvisory, TrailAdvisoryAPIData} from "../../../types/TrailAdvisory";
import WaypointMapper from "./WaypointMapper";
import {Geometry} from "geojson";
import {Site} from "../../../types/Site";
import {TrailRating} from "../../../types/TrailRating";
import Alert from "../../../components/Alert";
import {SubmitAction} from "../../../hooks/useSubmitActionRef";
import {urlTest} from "../../../services/validation-schemas";

// Constants ----

const transportIcons = {
  [TransportMode.CYCLING]: IconName.COG,
  [TransportMode.DRIVING]: IconName.TRUCK,
  [TransportMode.WALKING]: IconName.USER,
}

// Helpers ----

const trailTypeOptions = Object.values(TrailType).map(type => ({
  id: type.toLowerCase(),
  name: toTitleCase(type)
}))

const waypointOptions = (waypoints: TrailWaypoint[]) => waypoints.map((waypoint, index) => ({
  id: waypoint.id,
  name: `[${index + 1}] ${toTitleCase(waypoint.name)}`,
  icon: IconName.MAP
}))

const siteOptions = (sites: Site[] = []) => {
  return sites.map(site => ({
    id: site.id,
    name: site.name,
  }));
}

const ratingOptions = (ratings: TrailRating[] = []) => {
  return ratings.map(rating => ({
    id: rating.id,
    name: rating.name,
    colour: rating.colour,
  }));
}

const transportOptions = () => Object.values(TransportMode).map(id => ({
  id,
  name: toTitleCase(id),
  icon: transportIcons[id]
}))

const accessibilityOptions = () => Object.values(AccessibilityCriterion).map(id => ({
  id,
  name: toTitleCase(id),
  icon: IconName.HAND
}))

const sanitisedWaypointData = (values: TrailFormData) => {
  return values.waypoints?.map(waypoint => {
    const waypointCopy = {...waypoint};

    const uuidPattern = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/

    if(typeof waypointCopy.id !== 'number' && !waypointCopy.id.match(uuidPattern)) {
      // @ts-ignore
      waypointCopy.id = undefined;
    }

    return waypointCopy
  })
}

const extractAdvisoryData = (advisory: TrailAdvisory): TrailAdvisoryAPIData => {
  return {
    id: advisory.id,
    title: advisory.title,
    body: advisory.body,
    image_id: advisory.image?.id.toString(),
    order: advisory.order
  }
}

const commonValues = (trail?: Trail | null): TrailFormCommonData => ({
  name: trail?.name || '',
  description: trail?.description || '',
  site_id: trail?.site?.id.toString() || '',
  rating_id: trail?.rating?.id.toString() || '',
  rating_summary: trail?.rating_summary || '',
  transport_modes: trail?.transport_modes || [],
  accessibility_criteria: trail?.accessibility_criteria || [],
  duration: trail?.duration.as('seconds') || 0,
  policy_links: trail?.policy_links.map(link => ({
    id: link.id.toString(),
    text: link.text,
    url: link.url
  })) || [],
  advisories: trail?.advisories.map(extractAdvisoryData) || [],
  requirements: trail?.requirements || [],
  start_waypoint_id: trail?.start_waypoint?.id.toString() || '',
  end_waypoint_id: trail?.end_waypoint?.id.toString() || '',
  media_ids: trail?.media.map(media => media.id.toString()) || [],
  information: trail?.information.map(information => ({
    id: information.id,
    name: information.name,
    content: information.content,
    order: information.order
  })) || [],
  conclusion: trail?.conclusion || null,
  is_automatic: trail?.is_automatic ?? true,
  is_circuit: trail?.is_circuit ?? false,
  is_bidirectional: trail?.is_bidirectional ?? false,
  is_published: trail?.is_published ?? false,
  is_featured: trail?.is_featured ?? false,
  is_mapped: trail?.is_mapped ?? false,
  is_pinned: trail?.is_pinned ?? false,
})

const toStandardWaypointFormData = (waypoint: StandardTrailWaypoint) => ({
  id: waypoint.id,
  name: waypoint.name || '',
  description: waypoint.description || '',
  elevation: waypoint.elevation ?? 0,
  distance: waypoint.distance ?? 0,
  area: waypoint.area,
  position: waypoint.position,
  media_ids: waypoint.media.map(media => media.id.toString()) || [],
  exhibits: waypoint.exhibits || [],
  facilities: waypoint.facilities || [],
  offer_ids: waypoint.offers.map(offer => offer.id.toString()) || [],
  advisories: waypoint.advisories.map(extractAdvisoryData),
  order: waypoint.order
})

const toGolfWaypointFormData = (waypoint: GolfTrailWaypoint) => ({
  ...toStandardWaypointFormData(waypoint),
  par: waypoint.par,
})

const values = (trail?: Trail | null): TrailFormData => {
  if (trail?.type === TrailType.GOLF) {
    return {
      ...commonValues(trail),
      type: TrailType.GOLF,
      waypoints: trail.waypoints.map(toGolfWaypointFormData),
    }
  }

  return {
    ...commonValues(trail),
    type: TrailType.STANDARD,
    waypoints: trail?.waypoints.map(toStandardWaypointFormData) || [],
  }
}

const validationSchema = Yup.object().shape({
  type: Yup.string().max(250).required('This field is required'),
  name: Yup.string().max(250).required('This field is required').max(250, 'Maximum character limit exceeded'),
  description: Yup.string().nullable().max(2000, 'Maximum character limit exceeded'),
  rating_summary: Yup.string().nullable().max(250, 'Maximum character limit exceeded'),
  duration: Yup.number().min(0).max(4000000000).nullable(),
  policy_links: Yup.array().of(Yup.object().shape({
    text: Yup.string().required('This field is required').max(250, 'Maximum character limit exceeded'),
    url: Yup.string().required('This field is required').test('valid-url', urlTest),
  })),
  requirements: Yup.array().of(
    Yup.string().required('This field is required').max(250, 'Maximum character limit exceeded'),
  ),
  advisories: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required('This field is required').max(250, 'Maximum character limit exceeded'),
      body: Yup.string().max(20000, 'Maximum character limit exceeded'),
    })),
  information: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('This field is required').max(250, 'Maximum character limit exceeded'),
      content: Yup.string().required('This field is required').max(20000, 'Maximum character limit exceeded'),
    })),
  waypoints: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().nullable().required('This field is required').max(250, 'Maximum character limit exceeded'),
      description: Yup.string().nullable().max(2000, 'Maximum character limit exceeded'),
      elevation: Yup.number().integer().required(),
      distance: Yup.number().integer().required(),
      advisories: Yup.array().of(
        Yup.object().shape({
          title: Yup.string().required('This field is required').max(250, '250 characters maximum'),
        })),
    })
  ),
  start_waypoint_id: Yup.string().nullable(),
  end_waypoint_id: Yup.string().nullable(),
  conclusion: Yup.object().shape({
    title: Yup.string().required('This field is required').max(250, 'Maximum character limit exceeded'),
    body: Yup.string().required('This field is required').max(20000, 'Maximum character limit exceeded'),
  }).nullable(),
  is_circuit: Yup.boolean(),
  is_bidirectional: Yup.boolean(),
  is_automatic: Yup.boolean(),
  is_published: Yup.boolean(),
  is_mapped: Yup.boolean(),
  is_pinned: Yup.boolean(),
})

// Types ----

type TrailFormProps = {
  trail?: Trail | null
  onSubmit: (values: TrailFormData, helpers: FormikHelpers<TrailFormData>) => Promise<any>
  submitActionRef?: MutableRefObject<SubmitAction>;
}

// Component ----

function TrailForm({trail, onSubmit, submitActionRef}: TrailFormProps) {
  const {termFor} = useOperator();
  const auth = useAuth();

  const [mapFlyToGeometry, setMapFlyToGeometry] = useState<Geometry | undefined>();

  const {data: sites} = useQuery(
    'sites.all',
    () => ngage.sites.all(),
    {enabled: auth.can('sites:read')}
  );
  const {data: ratings} = useQuery(
    'trail-ratings.all',
    () => ngage.trailRatings.filtered(),
    {enabled: auth.can('trail-ratings:read')}
  );

  const waypointHelpersRef = useRef<FieldArrayRenderProps | null>(null);
  const [hasConclusion, setHasConclusion] = useState(!!trail?.conclusion ?? false);
  const [canFeature, setCanFeature] = useState<boolean>(trail?.is_published ?? false);
  const [canPin, setCanPin] = useState<boolean>(trail?.is_mapped ?? false);

  function handleConclusionToggle(checked: boolean, {setFieldValue}: FormikHelpers<TrailFormData>) {
    setHasConclusion(checked);

    if (checked) {
      void setFieldValue('conclusion', trail?.conclusion || {
        title: '',
        body: ''
      });
    } else {
      void setFieldValue('conclusion', null);
    }
  }

  const handlePublishedChanged = (isPublished: boolean) => {
    if (!isPublished) waypointHelpersRef.current?.form.setFieldValue('is_featured', false)
    setCanFeature(isPublished)
  }

  const handleMappedChanged = (isMapped: boolean) => {
    if (!isMapped) waypointHelpersRef.current?.form.setFieldValue('is_pinned', false)
    setCanPin(isMapped)
  }

  const handleSubmit = (values: TrailFormData, helpers: FormikHelpers<TrailFormData>) => {
    // Strip out any temporary values before sending
    return onSubmit({...values,
      waypoints: sanitisedWaypointData(values),
      conclusion: hasConclusion ? values.conclusion : null,
    } as TrailFormData, helpers)
  }

  // TODO: Work out the 'siteOption' type (it's not just 'Option')
  function handleSelectSite(siteOption: any) {
    // Find the site and fly to it on the map
    const site = sites?.data.find(site => site.id === Number(siteOption?.id));
    setMapFlyToGeometry(site?.area);
  }

  const waypointTerm = waypointHelpersRef.current?.form.values.type === TrailType.GOLF ? 'hole' : 'waypoint';

  return (
    <Form
      initialValues={values(trail)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      submitActionRef={submitActionRef}
    >
      <FormSection
        name="Key Details"
        description={`Enter descriptive details about the ${termFor('trail')}`}
        columns={6}
      >
        {/* Type */}
        <FormBlock columns={2}>
          <SelectField
            name="type"
            label="Trail Type *"
            help={`Changes how this ${termFor('trail')} is setup`}
            options={trailTypeOptions}
          />
        </FormBlock>

        {/* Name */}
        <FormBlock columns={4}>
          <TextField
            name="name"
            label="Name *"
            icon={TagIcon}
            hint="250 chars max"
            help={`This is how your audience will identify this ${termFor('trail')}`}
            autoComplete="off"
            maxLength={250}
          />
        </FormBlock>

        {/* Description */}
        <FormBlock columns={6}>
          <TextAreaField
            name="description"
            label="Description"
            hint="Optional. 2,000 chars max."
            help={`Briefly describe this ${termFor('trail')}, in a short, snappy way`}
            autoComplete="off"
            maxChars={2000}
            rows={6}
          />
        </FormBlock>

        {/* Rating */}
        <FormBlock columns={2}>
          <SelectField
            name="rating_id"
            label="Rating"
            hint="Optional"
            help={`Choose a rating for this ${termFor('trail')} from the list`}
            options={ratingOptions(ratings?.data)}
          />
        </FormBlock>

        {/* Rating Summary */}
        <FormBlock columns={6}>
          <TextAreaField
            name="rating_summary"
            label="Rating Summary"
            hint="Optional. 250 chars max."
            help={`Briefly summarise why this ${termFor('trail')} has been given its rating`}
            autoComplete="off"
            maxChars={250}
            rows={3}
          />
        </FormBlock>

        {/* Site */}
        <FormBlock columns={2}>
          <SelectField
            name="site_id"
            label={termFor('Site')}
            hint="Optional"
            help={`This is the site on which the ${termFor('trail')} starts`}
            options={siteOptions(sites?.data)}
            onChange={handleSelectSite}
          />
        </FormBlock>

        {/* Transport Modes */}
        <FormBlock columns={6} extraLargeColumns={3}>
          <MultiSelectField
            name="transport_modes"
            label="Transport Modes"
            options={transportOptions()}
          />
        </FormBlock>

        {/* Accessibility */}
        <FormBlock columns={6} extraLargeColumns={3}>
          <MultiSelectField
            name="accessibility_criteria"
            label="Accessibility"
            options={accessibilityOptions()}
          />
        </FormBlock>

        {/* Duration */}
        <FormBlock columns={6}>
          <DurationField
            name="duration"
            label="Duration"
            units={[DurationUnit.HOUR, DurationUnit.MINUTE]}
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Policies"
        description={`Links to any policies that should be read by audience members before attempting this ${termFor('trail')}.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <LinkListField name="policy_links" />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Requirements"
        description={`What audience members will need, or need to know, before they can attempt this ${termFor('trail')}.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <RequirementListField name="requirements" />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Advisories"
        description={`Information and warnings that will be shown to audience members when they start this ${termFor('trail')}.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <AdvisoryListField name="advisories" advisories={trail?.advisories ?? []}/>
        </FormBlock>
      </FormSection>

      <FormSection
        name="Information"
        description={`Add extra sections of information about this ${termFor('trail')}.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <InformationListField name="information"/>
        </FormBlock>
      </FormSection>

      <FormSection
        name="Media"
        description={`Add media that show the audience what the ${termFor('trail')} is like.`}
        columns={6}
      >
        <FormBlock columns={6}>
          <MediaField
            name="media_ids"
            files={trail?.media ?? []}
            maxFiles={20}
            type={[MediaField.types.AUDIO, MediaField.types.IMAGE, MediaField.types.VIDEO]}
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name={toTitleCase(waypointTerm + 's')}
        description={`Build up the ${termFor('trail')} by marking out ${waypointTerm} areas. At least 2 ${waypointTerm + 's'} are required to publish the ${termFor('trail')}.`}
        columns={6}
      >
        {/* Waypoint Mapper */}
        <FormBlock columns={6}>
          <div className="space-y-6">
            <WaypointMapper name="waypoints" trail={trail} flyToGeometry={mapFlyToGeometry}/>
          </div>
        </FormBlock>
      </FormSection>

      <FormSection
        name="Conclusion"
        description={`Customise the content that's shown to the audience member when they complete the ${termFor('trail')}`}
        columns={6}
      >
        <FormBlock columns={6}>
          {formik => (
            <ToggleSwitch
              label="Show Conclusion"
              description={`Whether to show a conclusion when the ${termFor('trail')} is completed`}
              multiline
              checked={hasConclusion}
              onChange={checked => handleConclusionToggle(checked, formik)}
            />
          )}
        </FormBlock>

        {hasConclusion &&
          <>
            <FormBlock columns={6}>
              <TextField
                name="conclusion.title"
                label="Title *"
                hint="250 chars max"
                autoComplete="off"
                maxLength={250}
              />
            </FormBlock>

            <FormBlock columns={6}>
              <TextAreaField
                name="conclusion.body"
                label="Body *"
                hint="20,000 chars max"
                autoComplete="off"
                maxChars={20000}
                rows={4}
              />
            </FormBlock>
          </>
        }
      </FormSection>

      <FormSection
        name="Configuration"
        description={`Adjust how the ${termFor('trail')} works when an audience member is attempting it.`}
        columns={6}
      >
        {!trail &&
          <FormBlock columns={6}>
            <Alert message="The start and end waypoints will automatically be set to your first and last waypoints respectively. If you'd like to change these you can do so after initial creation."  />
          </FormBlock>
        }

        {/* Start */}
        <FormBlock columns={3}>
          <SelectField
            name="start_waypoint_id"
            label="Start Waypoint"
            options={waypointOptions(trail?.waypoints ?? [])}
            disabled={!trail}
          />
        </FormBlock>

        {/* End */}
        <FormBlock columns={3}>
          <SelectField
            name="end_waypoint_id"
            label="End Waypoint"
            options={waypointOptions(trail?.waypoints ?? [])}
            disabled={!trail}
          />
        </FormBlock>

        {/* Is Circuit */}
        <FormBlock columns={6}>
          <ToggleField
            name="is_circuit"
            label="Circuit"
            description={`Whether or not this ${termFor('trail')} is a loop or circuit`}
          />
        </FormBlock>

        {/* Is Bidirectional */}
        <FormBlock columns={6}>
          <ToggleField
            name="is_bidirectional"
            label="Bidirectional"
            description={`Whether or not this ${termFor('trail')} can be attempted in either direction`}
          />
        </FormBlock>

        {/* Is Automatic */}
        <FormBlock columns={6}>
          <ToggleField
            name="is_automatic"
            label="Automatic"
            description={`Whether or not this ${termFor('trail')} will advance waypoints automatically`}
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Status"
        description={`Make this ${termFor('trail')} available for use, or keep it hidden.`}
        columns={6}
      >
        <FormBlock columns={6}>
          {(() => {
            const isDisabled = Number(waypointHelpersRef.current?.form.values.waypoints.length) < 2

            return (
              <ToggleField
                name="is_published"
                label="Published"
                disabled={isDisabled}
                description={isDisabled ?
                  `This ${termFor('trail')} needs at least 2 waypoints to be published` :
                  `Make this ${termFor('trail')} available to your audience`
                }
                onChange={handlePublishedChanged}
              />
            )
          })()}
        </FormBlock>

        {canFeature &&
          <FormBlock columns={6}>
            <ToggleField
              name="is_featured"
              label="Featured"
              description={`Highlight this ${termFor('trail')} for your audience`}
            />
          </FormBlock>
        }

        <FormBlock columns={6}>
          <ToggleField
            name="is_mapped"
            label="Show Position"
            description={`Show this ${termFor('trail')} on the map`}
            onChange={handleMappedChanged}
          />
        </FormBlock>

        {canPin &&
          <FormBlock columns={6}>
            <ToggleField
              name="is_pinned"
              label="Show pin"
              description={`Show a pin for this ${termFor('trail')} on the map`}
            />
          </FormBlock>
        }
      </FormSection>
    </Form>
  );
}

export default TrailForm;
